// PRE RMA — Import de saisies CPN (texte collé, fichier .csv / .txt / .tsv / .json). Fonctions PURES (aucun DOM, réseau ni IndexedDB).
// Même philosophie que importConsultations.ts : chaque ligne est convertie, validée avec le MÊME moteur que la saisie à la main
// (validerCpn + implications entre cases), et une ligne invalide est REFUSÉE avec sa raison — jamais corrigée en silence, jamais perdue.
//
// Formats acceptés :
//  1. Avec en-tête : « N° registre ; Âge ; CPN1 ; MMS ; TPI1 … » — une colonne par indicateur, case cochée = x / 1 / oui / true.
//  2. Sans en-tête : « 145 ; 22 ; CPN1 + MMS + TPI1 » — N° registre, âge, puis la liste des indicateurs (séparés par « | », « / » ou « + » entouré d'espaces).
//  3. JSON : [{ "numeroRegistre": 145, "ageAns": 22, "indicateurs": { "cpn1": true } }].
// Séparateur de colonnes : tabulation (copier-coller d'un tableur), point-virgule ou virgule.
import { CPN_INDICATEURS, CPN_INDICATEUR_LABELS, CPN_IMPLICATIONS, normaliserIndicateurs, validerCpn, ageAnsValide } from './cpn.ts';
import type { CpnIndicateur } from './cpn.ts';

export interface CpnImportee {
  numeroRegistre: number;
  ageAns: number;
  indicateurs: Record<CpnIndicateur, boolean>;
}
export interface ResultatImportCpn {
  valides: CpnImportee[];
  rejets: Array<{ ligne: number; raison: string }>;
  /** N° de registre déjà présents (appareil/serveur) ou répétés dans le fichier : ignorés, jamais écrasés. */
  doublons: number;
}

const compacter = (s: unknown): string =>
  String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/\+/g, ' plus ').replace(/<\s*/g, 'moins ').replace(/[^a-z0-9]+/g, '');

const INDEX: Map<string, CpnIndicateur> = (() => {
  const m = new Map<string, CpnIndicateur>();
  for (const cle of CPN_INDICATEURS) {
    for (const nom of [cle, CPN_INDICATEUR_LABELS[cle].label, CPN_INDICATEUR_LABELS[cle].court]) m.set(compacter(nom), cle);
  }
  // Variantes courantes d'écriture
  const alias: Array<[string, CpnIndicateur]> = [
    ['cpn5et plus', 'cpn5Plus'], ['cpn5 +', 'cpn5Plus'], ['cpn5+', 'cpn5Plus'], ['tpi4et plus', 'tpi4Plus'], ['tpi 4+', 'tpi4Plus'],
    ['td2+', 'td2Plus'], ['td2 et plus', 'td2Plus'], ['cpn precoce', 'cpnPrecoce'], ['palu+', 'palu'], ['paludisme', 'palu'],
  ];
  for (const [a, c] of alias) m.set(compacter(a), c);
  return m;
})();

/** Nom d'indicateur (clé, libellé, abréviation ou variante) → clé officielle ; null si inconnu. */
export function reconnaitreIndicateurCpn(nom: string): CpnIndicateur | null {
  return INDEX.get(compacter(nom)) ?? null;
}

const COCHE = new Set(['x', '1', 'oui', 'o', 'true', 'vrai', 'yes']);
const estCocheTexte = (v: string) => COCHE.has(v.trim().toLowerCase());

/** Applique les implications (CPN précoce ⇒ CPN1, syphilis+ ⇒ test syphilis, VIH+ ⇒ test VIH), comme l'écran de saisie. */
function appliquerImplications(ind: Record<CpnIndicateur, boolean>): Record<CpnIndicateur, boolean> {
  const out = { ...ind };
  for (const [source, cible] of Object.entries(CPN_IMPLICATIONS) as Array<[CpnIndicateur, CpnIndicateur]>) {
    if (out[source]) out[cible] = true;
  }
  return out;
}

function decouper(ligne: string): string[] {
  const sep = ligne.includes('\t') ? '\t' : ligne.includes(';') ? ';' : ',';
  return ligne.split(sep).map((c) => c.trim());
}

function nombreStrict(v: string): number | null {
  const t = v.trim();
  return /^\d+$/.test(t) ? Number(t) : null;
}

type LigneCpnBrute = { registre: string; age: string; indicateurs: string[]; cochees: Partial<Record<CpnIndicateur, boolean>> };

function depuisJson(contenu: string): LigneCpnBrute[] | null {
  const t = contenu.trim();
  if (!t.startsWith('[')) return null;
  try {
    const v = JSON.parse(t);
    if (!Array.isArray(v)) return null;
    return v.map((o: any) => ({
      registre: String(o?.numeroRegistre ?? o?.registre ?? '').trim(),
      age: String(o?.ageAns ?? o?.age ?? '').trim(),
      indicateurs: [],
      cochees: normaliserIndicateurs(o?.indicateurs ?? null),
    }));
  } catch { return null; }
}

function depuisTexte(contenu: string): LigneCpnBrute[] {
  const lignes = contenu.replace(/\r/g, '').split('\n').filter((l) => l.trim() !== '');
  let colonnes: Array<CpnIndicateur | 'registre' | 'age' | null> | null = null;
  const out: LigneCpnBrute[] = [];
  for (const l of lignes) {
    const c = decouper(l);
    const n = c.map(compacter);
    // En-tête : contient « registre » (ou « n° ») ET « age »
    if (n.some((x) => x.includes('registre') || x === 'n' || x === 'no') && n.some((x) => x === 'age' || x === 'agean' || x === 'ageans' || x === 'agesans')) {
      colonnes = n.map((x) => (x.includes('registre') || x === 'n' || x === 'no' ? 'registre' : x.startsWith('age') ? 'age' : reconnaitreIndicateurCpn(x)));
      continue;
    }
    if (colonnes) {
      const cochees: Partial<Record<CpnIndicateur, boolean>> = {};
      let registre = '', age = '';
      c.forEach((v, i) => {
        const col = colonnes![i];
        if (col === 'registre') registre = v;
        else if (col === 'age') age = v;
        else if (col && estCocheTexte(v)) cochees[col] = true;
      });
      out.push({ registre, age, indicateurs: [], cochees });
    } else {
      // Sans en-tête : registre ; âge ; liste d'indicateurs (dans la 3e cellule, ou les cellules suivantes)
      // « + » sépare deux indicateurs seulement s'il est entouré d'espaces (« CPN1 + MMS ») : « CPN5+ » ou « TPI 4 et + » restent entiers.
      const reste = c.slice(2).join('|');
      out.push({ registre: c[0] ?? '', age: c[1] ?? '', indicateurs: reste.split(/\s*[|/]\s*|\s+\+\s+(?=\S)/).map((x) => x.trim()).filter(Boolean), cochees: {} });
    }
  }
  return out;
}

/** Analyse complète sans rien écrire : extraction, conversion, validation, doublons. `existants` = N° de registre déjà saisis pour la période. */
export function analyserImportCpn(contenu: string, existants: Iterable<number> = []): ResultatImportCpn {
  const brutes = depuisJson(contenu) ?? depuisTexte(contenu ?? '');
  const deja = new Set<number>(existants);
  const res: ResultatImportCpn = { valides: [], rejets: [], doublons: 0 };
  brutes.forEach((b, i) => {
    const ligne = i + 1;
    const registre = nombreStrict(b.registre);
    const age = ageAnsValide(b.age.trim() === '' ? null : b.age);
    const ind = normaliserIndicateurs(b.cochees);
    for (const nom of b.indicateurs) {
      const cle = reconnaitreIndicateurCpn(nom);
      if (!cle) { res.rejets.push({ ligne, raison: `Indicateur inconnu : « ${nom} »` }); return; }
      ind[cle] = true;
    }
    const v = validerCpn({ numeroRegistre: registre, ageAns: age });
    if (!v.ok) { res.rejets.push({ ligne, raison: v.erreurs[0] ?? 'Saisie invalide' }); return; }
    if (deja.has(registre!)) { res.doublons++; return; }
    if (!CPN_INDICATEURS.some((k) => ind[k])) { res.rejets.push({ ligne, raison: 'Aucun indicateur coché' }); return; }
    deja.add(registre!);
    res.valides.push({ numeroRegistre: registre!, ageAns: age!, indicateurs: appliquerImplications(ind) });
  });
  return res;
}
